import React from 'react';
import { ArrowRightIcon, CalendarDaysIcon, CheckIcon } from 'lucide-react';
import { PageShell } from '../components/layout/PageShell';
import { InquiryForm } from '../components/forms/InquiryForm';
import { ButtonLink } from '../components/ui/ButtonLink';
import { programs } from '../data/programs';
import { site } from '../data/site';
import type { ProgramId } from '../types/content';
const programRoutes: Record<ProgramId, string> = {
  'little-braves': '/little-braves',
  kids: '/kids-taekwondo',
  'teens-adults': '/teens-adults',
  'competition-team': '/competition-team'
};
const programHeroPhotos: Record<ProgramId, {
  src: string;
  alt: string;
  position: string;
  mobilePosition: string;
  credit?: string;
}> = {
  'little-braves': {
    src: 'https://static.wixstatic.com/media/37c34b_6fdec3490f074c288a1ce8cc078d51be~mv2.jpeg',
    alt: 'Four young Bates Taekwondo students smiling in protective gear',
    position: '50% 30%',
    mobilePosition: '42% 50%'
  },
  kids: {
    src: 'https://static.wixstatic.com/media/37c34b_12383dcdf7c44ec492a8e2a0d5e41306~mv2.jpg',
    alt: 'A Bates coach kneeling beside a young student in protective Taekwondo gear',
    position: '50% 45%',
    mobilePosition: '53% 50%'
  },
  'teens-adults': {
    src: 'https://static.wixstatic.com/media/61e59f_37f5fba21ed14ce5a207a9a9807160bf~mv2.jpg',
    alt: 'Two Taekwondo practitioners working on a high kick with a training paddle',
    position: '50% 35%',
    mobilePosition: '90% 50%',
    credit: 'Photo: Gomez PhotoWorks'
  },
  'competition-team': {
    src: 'https://static.wixstatic.com/media/37c34b_67ed01afa9d64739aee7738ef05d143e~mv2.jpg',
    alt: 'Three young Bates Taekwondo athletes wearing medals beside the tournament mats',
    position: '50% 25%',
    mobilePosition: '45% 50%'
  }
};
const programGalleries: Partial<Record<ProgramId, {
  src: string;
  alt: string;
  width: number;
  height: number;
}[]>> = {
  'little-braves': [{
    src: 'https://static.wixstatic.com/media/37c34b_8be34a4d8bbe4013b926f58775df3394~mv2.jpg',
    alt: 'Two Little Braves students wearing red chest protectors on the training mats',
    width: 1262,
    height: 1264
  }],
  'competition-team': [{
    src: 'https://static.wixstatic.com/media/37c34b_4459babc4242442c8ac91ea31ac60ab5~mv2.jpg',
    alt: 'Two Taekwondo athletes exchanging kicks during a tournament match',
    width: 649,
    height: 543
  }, {
    src: 'https://static.wixstatic.com/media/37c34b_c783d22243584c0cbf22d277dd342ff5~mv2.png',
    alt: 'Four people in matching blue Taekwondo team shirts posing together at an event',
    width: 2038,
    height: 1283
  }, {
    src: 'https://static.wixstatic.com/media/37c34b_67ed01afa9d64739aee7738ef05d143e~mv2.jpg',
    alt: 'Three young Taekwondo athletes wearing medals beside the tournament mats',
    width: 2048,
    height: 1536
  }, {
    src: 'https://static.wixstatic.com/media/37c34b_5b331a88ef0141a9876fb8a8912cfbc7~mv2.jpg',
    alt: 'Bates team photo with AAU Nationals, Florida 2023 and USA Nationals lettering',
    width: 2048,
    height: 1365
  }]
};
export function ProgramPage({
  programId
}: {
  programId: ProgramId;
}) {
  const program = programs.find(item => item.id === programId)!;
  const gallery = programGalleries[programId];
  const heroPhoto = programHeroPhotos[programId];
  const isCompetition = programId === 'competition-team';
  const contactHref = isCompetition ? `${site.emailHref}?subject=${encodeURIComponent('Competition Team inquiry')}` : '/free-trial-class';
  return <PageShell title={program.name} eyebrow={program.audience} intro={program.summary} breadcrumbs={[{
    label: 'Programs',
    href: '/#programs'
  }, {
    label: program.name
  }]} heroBackground={heroPhoto} heroAside={<InquiryForm context={program.name} data-id="element-722" data-mp="b5veilpzsg2g" />} data-id="element-721" data-mp="2vv2ojdpywfbm">
      <section className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16" aria-labelledby="program-training-title" data-id="element-723" data-mp="63xptbytot24">
        <div data-id="element-724" data-mp="11gp3joa8y8tu">
          <h2 id="program-training-title" className="font-display text-3xl font-bold uppercase leading-tight sm:text-4xl" data-id="element-725" data-mp="11h8l33vziv7c">
            {isCompetition ? 'Train with a competitive goal' : 'What happens in class'}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted" data-id="element-726" data-mp="1gcesnvaqb22s">{program.description}</p>
          <ul className="mt-7 space-y-4" data-id="element-727" data-mp="1ga23oy6jzrg8">
            {program.practices.map(practice => <li key={practice} className="flex items-start gap-3 leading-relaxed" data-id="element-728" data-mp="1kc5oidf448e6">
                <CheckIcon className="mt-1 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-729" data-mp="1pb07pggzryyt" />
                {practice}
              </li>)}
          </ul>
          <div className="mt-8 border-t border-line pt-6" data-id="element-730" data-mp="1z1uwoslmhcny">
            <h3 className="text-sm font-semibold uppercase tracking-[0.12em]" data-id="element-731" data-mp="24293y8fegikx">A good fit for</h3>
            <p className="mt-3 leading-relaxed text-muted" data-id="element-732" data-mp="283knpxble9l7">{program.goodFit}</p>
          </div>
          <ButtonLink href={contactHref} className="mt-8 w-full !whitespace-normal py-3 text-center sm:w-auto" icon={<ArrowRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-734" data-mp="3ok9ue5ogtnd8" />} data-id="element-733" data-mp="3sln3comuu6od">
            {program.ctaLabel}
          </ButtonLink>
        </div>
        <figure className="overflow-hidden rounded bg-coal" data-id="element-735" data-mp="3jlqaeatfmlni">
          <img src={program.image} alt={program.imageAlt} className={`aspect-[4/3] w-full ${program.imageFit === 'contain' ? 'object-contain' : 'object-cover'}`} style={{
          objectPosition: program.imagePosition
        }} data-id="element-736" data-mp="3emdlzlmwx6it" />
          <figcaption className="border-t border-white/10 px-5 py-4 text-sm text-white/75" data-id="element-737" data-mp="b7hmvh5rol27">
            {program.name} at Bates Taekwondo in Albuquerque
          </figcaption>
        </figure>
      </section>

      {programId === 'little-braves' && <section aria-labelledby="little-braves-progress-title" className="grid gap-8 border-t border-line pt-10 lg:grid-cols-2 lg:gap-16" data-id="element-738" data-mp="yvrbsrya1oxk">
          <div data-id="element-739" data-mp="u1wozfndktu9">
            <p className="text-sm font-semibold uppercase tracking-[0.12em] text-brand" data-id="element-740" data-mp="2obtlpb8okv1v">Small steps. Growing independence.</p>
            <h2 id="little-braves-progress-title" className="mt-3 font-display text-3xl font-bold uppercase sm:text-4xl" data-id="element-741" data-mp="2sdooyla69iia">A foundation for ages 3–5</h2>
            <p className="mt-5 leading-relaxed text-muted" data-id="element-742" data-mp="2ejxo01aib6y6">Children work on posture, balance and coordination while learning to follow directions. Four introductory belt ranks give them achievable goals as they grow more comfortable trying skills on their own.</p>
          </div>
          <div className="rounded border border-line bg-white p-6 sm:p-8" data-id="element-743" data-mp="29oichfzz1n4x">
            <h3 className="flex items-center gap-3 font-display text-2xl font-semibold uppercase" data-id="element-744" data-mp="1vo7mef6hkckt"><CalendarDaysIcon className="h-6 w-6 text-brand" aria-hidden="true" data-id="element-745" data-mp="20lr11g73irda" />Little Braves class options</h3>
            <dl className="mt-5 space-y-4 text-[15px]" data-id="element-746" data-mp="3q28kgxu7ovju">
              <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 border-b border-line pb-3" data-id="element-747" data-mp="3v0a0f9wccer3"><dt className="font-semibold" data-id="element-748" data-mp="3uxxmr8xm8qju">Group 1 · Mon & Wed</dt><dd data-id="element-749" data-mp="8zvx6htc83gh">4:30 p.m.</dd></div>
              <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 border-b border-line pb-3" data-id="element-750" data-mp="8wq995wg37mc"><dt className="font-semibold" data-id="element-751" data-mp="41km63rb3mwo">Group 2 · Tue & Thu</dt><dd data-id="element-752" data-mp="hro0m8jy2cbk">4:30 p.m.</dd></div>
              <div data-id="element-753" data-mp="hspj2y3ai13b"><dt className="font-semibold" data-id="element-754" data-mp="hsfo0w3e13qr">Both groups · Friday, 4:30 p.m.</dt><dd className="mt-1 text-muted" data-id="element-755" data-mp="d065i14lr5yi">Sparring · gear required</dd></div>
            </dl>
            <p className="mt-5 text-sm leading-relaxed text-muted" data-id="element-756" data-mp="roj12wzs2msh">Contact us to confirm current availability and the right group for your child.</p>
          </div>
        </section>}

      {isCompetition && <section aria-labelledby="competition-path-title" className="grid gap-8 border-t border-line pt-10 lg:grid-cols-2 lg:gap-16" data-id="element-757" data-mp="10opd0eftvepj">
          <div data-id="element-758" data-mp="wmkjdfpdqi2w">
            <p className="text-sm font-semibold uppercase tracking-[0.12em] text-brand" data-id="element-759" data-mp="32hdeai8wvs2o">An additional pathway</p>
            <h2 id="competition-path-title" className="mt-3 font-display text-3xl font-bold uppercase sm:text-4xl" data-id="element-760" data-mp="2yga1y5q2oe67">From the training floor to the tournament</h2>
            <p className="mt-5 leading-relaxed text-muted" data-id="element-761" data-mp="3mym24f1yigqj">Team training includes focused sparring preparation and opportunities to compete around the country. Athletes put in committed work alongside teammates and build friendships along the way.</p>
          </div>
          <div className="rounded border border-line bg-white p-6 sm:p-8" data-id="element-762" data-mp="4lfkm2y3jueu">
            <h3 className="font-display text-2xl font-semibold uppercase" data-id="element-763" data-mp="342dy1ekokbwt">Interested in joining?</h3>
            <p className="mt-4 leading-relaxed text-muted" data-id="element-764" data-mp="3905lnwealbv7">Talk with a coach about your experience, competitive goals, current training times and next steps.</p>
            <a href={site.phoneHref} className="mt-5 inline-flex min-h-[44px] items-center font-semibold text-brand underline underline-offset-4" data-id="element-765" data-mp="3d4vibngryuh6">Call {site.phoneDisplay}</a>
          </div>
        </section>}

      {gallery && <section aria-labelledby="program-gallery-title" data-id="element-766" data-mp="xalfrxyfkwis">
          <h2 id="program-gallery-title" className="font-display text-3xl font-bold uppercase sm:text-4xl" data-id="element-767" data-mp="xd7mjvdhz7zy">
            {isCompetition ? 'Team moments' : 'Little Braves on the mat'}
          </h2>
          <div className={`mt-7 grid gap-5 ${gallery.length > 1 ? 'sm:grid-cols-2' : 'max-w-2xl'}`} data-id="element-768" data-mp="12ibcfv4t71wy">
            {gallery.map(photo => <a key={photo.src} href={photo.src} target="_blank" rel="noopener noreferrer" aria-label={`Open full photo: ${photo.alt} (opens in a new tab)`} className="flex items-center justify-center overflow-hidden rounded border border-line bg-coal" data-id="element-769" data-mp="toq0ejk13yr8">
                <img src={photo.src} alt={photo.alt} width={photo.width} height={photo.height} loading="lazy" className={`w-full object-contain ${gallery.length > 1 ? 'aspect-[4/3]' : 'h-auto'}`} data-id="element-770" data-mp="esh15i051let" />
              </a>)}
          </div>
        </section>}

      <section className="grid gap-6 sm:grid-cols-2" aria-label="Plan your training" data-id="element-771" data-mp="1eh9wikq6wt97">
        <a href="/schedule" className="group rounded border border-line bg-white p-6 transition-colors hover:border-ink sm:p-8" data-id="element-772" data-mp="1afwqa9j6qo1r">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] text-brand" data-id="element-773" data-mp="1jerwvmq5605l">Plan your week</p>
          <h2 className="mt-3 flex items-center justify-between gap-3 font-display text-2xl font-bold uppercase" data-id="element-774" data-mp="voo3e86o3gsb">Class schedule <ArrowRightIcon className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-775" data-mp="qqmbqyv6e958" /></h2>
          <p className="mt-3 leading-relaxed text-muted" data-id="element-776" data-mp="15gbozsgjm3lv">See the school schedule and confirm the class that fits.</p>
        </a>
        <a href={programId === 'little-braves' ? '/free-trial-class' : '/more'} className="group rounded border border-line bg-white p-6 transition-colors hover:border-ink sm:p-8" data-id="element-777" data-mp="10m7itbf3pzlu">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] text-brand" data-id="element-778" data-mp="7z3b2yomj76d">{programId === 'little-braves' ? 'Get started' : 'Practice between classes'}</p>
          <h2 className="mt-3 flex items-center justify-between gap-3 font-display text-2xl font-bold uppercase" data-id="element-779" data-mp="2oegoc3t5bq6s">{programId === 'little-braves' ? 'Your first class' : 'Poomsae videos'} <ArrowRightIcon className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-780" data-mp="2enc7szntnx9u" /></h2>
          <p className="mt-3 leading-relaxed text-muted" data-id="element-781" data-mp="32e9dhup0xycn">{programId === 'little-braves' ? 'Arrange a free trial and meet the coaches.' : 'Review forms and belt-test practice resources.'}</p>
        </a>
      </section>

      <nav aria-label="Other Taekwondo programs" className="border-t border-line pt-8" data-id="element-782" data-mp="2tc01f0e3xwx5">
        <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-muted" data-id="element-783" data-mp="2ya9xdd7np1jt">Explore our other programs</h2>
        <div className="mt-4 flex flex-wrap gap-x-7 gap-y-2" data-id="element-784" data-mp="1hw1mwh05uioj">
          {programs.filter(item => item.id !== programId).map(item => <a key={item.id} href={programRoutes[item.id]} className="inline-flex min-h-[44px] items-center gap-2 font-semibold underline underline-offset-4 hover:text-brand" data-id="element-785" data-mp="3olc051z58nh4">{item.name}<ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-786" data-mp="3joljodjffpv6" /></a>)}
        </div>
      </nav>
    </PageShell>;
}